import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { SharedModule } from '@Shared/shared.module';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { CaseCycleTime, SlaAdherence, QueueCycleTime, TaskCycleTime, TeamMemberCycleTime, TeamMemberSlaAdherence, TeamPerformanceData } from '@Models'
import { AvgCaseCycleTimeLineChartComponent, AvgCaseCycleTimeLineChartData } from './cases/avg-case-cycle-time-line-chart/avg-case-cycle-time-line-chart.component';
import { CaseSlaAdherenceChartComponent } from './cases/case-sla-adherence-chart/case-sla-adherence-chart.component';
import { CaseCycleTimePerQueueLineChartComponent } from './cases/case-cycle-time-per-queue-line-chart/case-cycle-time-per-queue-line-chart.component';
import { CaseCycleTimePerQueueGroupedBarChartComponent } from './cases/case-cycle-time-per-queue-grouped-bar-chart/case-cycle-time-per-queue-grouped-bar-chart.component';
import { TaskCycleTimeHeatMapComponent, TaskCycleTimeBarSeries } from './tasks/task-cycle-time-heat-map/task-cycle-time-heat-map.component';
import { TaskSixMonthBarChartComponent } from './tasks/task-six-month-bar-chart/task-six-month-bar-chart.component';
import { TaskCycleTimeBarChartComponent } from './tasks/task-cycle-time-bar-chart/task-cycle-time-bar-chart.component';

@Component({
  selector: 'team-performance-component',
  templateUrl: './team-performance.component.html',
  styleUrl: './team-performance.component.scss',
  imports: [
        SharedModule,
        MatButtonToggleModule,
        AvgCaseCycleTimeLineChartComponent,
        CaseSlaAdherenceChartComponent,
        CaseCycleTimePerQueueLineChartComponent,
        CaseCycleTimePerQueueGroupedBarChartComponent,
        TaskCycleTimeHeatMapComponent,
        TaskSixMonthBarChartComponent,
        TaskCycleTimeBarChartComponent
    ]
})
export class TeamPerformanceComponent implements OnInit, OnChanges {
    @Input() rawPerformanceData: TeamPerformanceData | null = null;
    @Input() accountFilter: string[] = [];
    avgCaseCycleTime6Months: AvgCaseCycleTimeLineChartData[] = [];
    caseSlaAdherenceData: SlaAdherence[] = [];
    caseQueueCycleTimes: QueueCycleTime[] = [];
    teamMemberAvgCycleTime6Months: TeamMemberCycleTime[] = [];
    taskCycleTimeByTaskMonthLabels: string[] = [];
    taskCycleTimeByTaskSeries: TaskCycleTimeBarSeries[] = [];
    avgCaseCycleTimeEntityType: 'account' | 'teamMember' = 'account';
    caseSlaAdherenceEntityType: 'queue' | 'teamMember' = 'queue';
    averageCycleTimeAccountTooltip = 'Account cycle time is the average duration of all cases, from start to completion.';
    averageCycleTimeTeamMemberTooltip = 'Team member cycle time is the average duration of all cases last assigned to the team member.';
    averageCycleTimeQueueTooltip = 'Queue cycle time is the average duration of all cases in a queue, from entry to exit.';
    slaAdherenceQueueTooltip = 'Queue SLA adherence = (cases completed within SLA ÷ total completed cases) × 100';
    slaAdherenceTeamMemberTooltip = 'Team member SLA adherence = (cases completed within SLA by team member ÷ total cases completed by team member) × 100';
    averageTaskCycleTimeTooltip = 'Average Task cycle time is the average duration of all tasks, from start to completion.';

    expandedChart: number | null = null;
    caseAvgCycleTimePerQueueChartType: 'line' | 'bar' = 'line';
    taskCycleTimeChartType: 'byTask' | 'byMonth' = 'byTask';
    @ViewChild('caseAvgCycleTimeChart') caseAvgCycleTimeChart: AvgCaseCycleTimeLineChartComponent;
    @ViewChild('caseCycleTimePerQueueLineChart') caseCycleTimePerQueueLineChart: CaseCycleTimePerQueueLineChartComponent;
    @ViewChild('caseCycleTimePerQueueBarChart') caseCycleTimePerQueueBarChart: CaseCycleTimePerQueueGroupedBarChartComponent;
    @ViewChild('taskHeatMap') taskHeatMap: TaskCycleTimeHeatMapComponent;
    @ViewChild('taskSixMonthChart') taskSixMonthChart: TaskSixMonthBarChartComponent;
    @ViewChild('taskByMonthChart') taskByMonthChart: TaskCycleTimeBarChartComponent;

    constructor() {}

    ngOnInit(): void {
        this.loadAvgCycleTimePerQueueChartType();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['rawPerformanceData'] || changes['accountFilter']) {
            this.applyAllChartData();
        }
    }

    applyAllChartData(): void {
        this.applyAvgCaseCycleTimeData();
        this.applyCaseSlaAdherenceData();
        this.applyCaseQueueCycleTimeData();
        this.applyAvgTaskCycleTimeData();
    }

    private filterByAccount<T extends { parentAccountId?: string }>(items: T[]): T[] {
        if (!this.accountFilter?.length) return items;
        return items.filter(item =>
            this.accountFilter.some(id => (item.parentAccountId || '').toLowerCase() === id.toLowerCase())
        );
    }

    rollupCaseSlaAdherenceByQueue(items: SlaAdherence[]): SlaAdherence[] {
        const map = new Map<string, SlaAdherence>();
        for (const item of items) {
            const existing = map.get(item.queueName);
            if (existing) {
                existing.totalCasesExited += item.totalCasesExited;
                existing.casesMetSLA += item.casesMetSLA;
                existing.slaAdherencePercentage = existing.totalCasesExited > 0
                    ? Math.round(existing.casesMetSLA / existing.totalCasesExited * 10000) / 100
                    : 0;
            } else {
                map.set(item.queueName, { ...item });
            }
        }
        return Array.from(map.values());
    }

    rollupCaseSlaAdherenceByTeamMember(items: TeamMemberSlaAdherence[]): TeamMemberSlaAdherence[] {
        const map = new Map<string, TeamMemberSlaAdherence>();
        for (const item of items) {
            const existing = map.get(item.userId);
            if (existing) {
                existing.totalQueueCount += item.totalQueueCount;
                existing.metQueueCount += item.metQueueCount;
                existing.casesWithSlaQueues += item.casesWithSlaQueues;
                existing.slaAdherencePercentage = existing.totalQueueCount > 0
                    ? Math.round(existing.metQueueCount / existing.totalQueueCount * 10000) / 100
                    : 0;
            } else {
                map.set(item.userId, { ...item });
            }
        }
        return Array.from(map.values());
    }

    rollupCaseTeamMemberCycleTimes(items: TeamMemberCycleTime[]): TeamMemberCycleTime[] {
        const map = new Map<string, { sum: number; count: number; item: TeamMemberCycleTime }>();
        for (const item of items) {
            const key = `${item.userId}-${item.doneYear}-${item.doneMonth}`;
            const existing = map.get(key);
            if (existing) {
                existing.sum += item.avgCycleTime ?? 0;
                existing.count += 1;
            } else {
                map.set(key, { sum: item.avgCycleTime ?? 0, count: 1, item: { ...item } });
            }
        }
        return Array.from(map.values()).map(({ sum, count, item }) => ({
            ...item,
            avgCycleTime: count > 0 ? sum / count : 0
        }));
    }

    rollupCaseQueueCycleTimes(items: QueueCycleTime[]): QueueCycleTime[] {
        const map = new Map<string, { weightedSum: number; totalCases: number; item: QueueCycleTime }>();
        for (const item of items) {
            const key = `${item.queueId}-${item.exitYear}-${item.exitMonth}`;
            const existing = map.get(key);
            if (existing) {
                existing.weightedSum += (item.avgQueueCycleTimeInHours ?? 0) * item.totalCasesExited;
                existing.totalCases += item.totalCasesExited;
            } else {
                map.set(key, {
                    weightedSum: (item.avgQueueCycleTimeInHours ?? 0) * item.totalCasesExited,
                    totalCases: item.totalCasesExited,
                    item: { ...item }
                });
            }
        }
        return Array.from(map.values()).map(({ weightedSum, totalCases, item }) => ({
            ...item,
            totalCasesExited: totalCases,
            avgQueueCycleTimeInHours: totalCases > 0 ? weightedSum / totalCases : 0
        }));
    }

    onAvgCaseCycleTimeEntityTypeChange(value: 'account' | 'teamMember'): void {
        if (this.avgCaseCycleTimeEntityType === value) {
            return;
        }

        this.avgCaseCycleTimeEntityType = value;
        this.applyAvgCaseCycleTimeData();
    }

    onCaseSlaAdherenceEntityTypeChange(value: 'queue' | 'teamMember'): void {
        if (this.caseSlaAdherenceEntityType === value) {
            return;
        }

        this.caseSlaAdherenceEntityType = value;
        this.applyCaseSlaAdherenceData();
    }

    applyCaseSlaAdherenceData(): void {
        if (!this.rawPerformanceData) {
            this.caseSlaAdherenceData = [];
            return;
        }

        if (this.caseSlaAdherenceEntityType === 'queue') {
            const filtered = this.filterByAccount(this.rawPerformanceData.slaAdherence || []);
            this.caseSlaAdherenceData = this.rollupCaseSlaAdherenceByQueue(filtered);
        } else if (this.caseSlaAdherenceEntityType === 'teamMember') {
            const filtered = this.filterByAccount(this.rawPerformanceData.teamMemberSlaAdherence || []);
            const rolledUp = this.rollupCaseSlaAdherenceByTeamMember(filtered);
            const sorted = this.sortByTeamMemberName(rolledUp);
            this.caseSlaAdherenceData = this.mapTeamMemberSlaAdherenceToSlaAdherenceData(sorted);
        }
    }

    mapTeamMemberSlaAdherenceToSlaAdherenceData(teamMemberSlaAdherenceData: TeamMemberSlaAdherence[]): SlaAdherence[] {
        return (teamMemberSlaAdherenceData || []).map((teamMemberSlaAdherence: TeamMemberSlaAdherence) => {
            const fullName = `${teamMemberSlaAdherence.firstName || ''} ${teamMemberSlaAdherence.lastName || ''}`.trim();

            return {
                clientId: teamMemberSlaAdherence.clientId,
                teamId: teamMemberSlaAdherence.teamId,
                parentAccountId: teamMemberSlaAdherence.parentAccountId,
                accountCode: teamMemberSlaAdherence.accountCode,
                accountName: teamMemberSlaAdherence.accountName,
                queueName: fullName,
                slaInHours: 0,
                totalCasesExited: teamMemberSlaAdherence.totalQueueCount,
                casesMetSLA: teamMemberSlaAdherence.metQueueCount,
                slaAdherencePercentage: teamMemberSlaAdherence.slaAdherencePercentage
            };
        });
    }

    applyAvgCaseCycleTimeData(): void {
        if (!this.rawPerformanceData) {
            this.avgCaseCycleTime6Months = [];
            this.teamMemberAvgCycleTime6Months = [];
            return;
        }

        if (this.avgCaseCycleTimeEntityType === 'account') {
            const filtered = this.filterByAccount(this.rawPerformanceData.avgCaseCycleTime6Months || []);
            this.avgCaseCycleTime6Months = this.mapCasesToCycleTimeChartData(filtered);
        } else if (this.avgCaseCycleTimeEntityType === 'teamMember') {
            const filtered = this.filterByAccount(this.rawPerformanceData.teamMemberAvgCycleTime6Months || []);
            const rolledUp = this.rollupCaseTeamMemberCycleTimes(filtered);
            const sorted = this.sortByTeamMemberName(rolledUp);
            this.avgCaseCycleTime6Months = this.mapTeamMembersToCycleTimeChartData(sorted);
        }
    }

    applyCaseQueueCycleTimeData(): void {
        if (!this.rawPerformanceData) {
            this.caseQueueCycleTimes = [];
            return;
        }
        const filtered = this.filterByAccount(this.rawPerformanceData.queueCycleTimes || []);
        this.caseQueueCycleTimes = this.rollupCaseQueueCycleTimes(filtered);
    }

    applyAvgTaskCycleTimeData(): void {
        if (!this.rawPerformanceData) {
            this.taskCycleTimeByTaskMonthLabels = [];
            this.taskCycleTimeByTaskSeries = [];
            return;
        }
        const filtered = this.filterByAccount(this.rawPerformanceData.avgTaskCycleTime6Months || []);
        const grouped = this.aggregateTasksByTaskAndMonth(filtered);
        this.taskCycleTimeByTaskMonthLabels = grouped.monthLabels;
        this.taskCycleTimeByTaskSeries = grouped.series;
    }

    mapTeamMembersToCycleTimeChartData(teamMemberCycleTimes: TeamMemberCycleTime[]): AvgCaseCycleTimeLineChartData[] {
        return (teamMemberCycleTimes || []).map((member: TeamMemberCycleTime) => {
            const fullName = member.firstName + ' ' + member.lastName;
            return {
                id: member.userId,
                fullLabel: fullName,
                shortLabel: fullName,
                doneYear: member.doneYear ?? 0,
                doneMonth: member.doneMonth ?? 0,
                avgCycleTime: member.avgCycleTime ?? 0
            };
        });
    }

    mapCasesToCycleTimeChartData(accounts: CaseCycleTime[]): AvgCaseCycleTimeLineChartData[] {
        return (accounts || []).map((account: CaseCycleTime) => {
            return {
                id: account.parentAccountId,
                fullLabel: account.accountName,
                shortLabel: account.accountAbbreviation || account.accountName.slice(0, 3).toUpperCase(),
                doneYear: account.doneYear,
                doneMonth: account.doneMonth,
                avgCycleTime: account.avgCycleTime
            };
        });
    }

    aggregateTasksByTaskAndMonth(tasks: TaskCycleTime[]): { monthLabels: string[]; series: TaskCycleTimeBarSeries[] } {
        if (!tasks?.length) return { monthLabels: [], series: [] };
        const monthAbbr = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

        const presentKeys = tasks.map(t => t.doneYear * 12 + (t.doneMonth - 1));
        const maxKey = Math.max(...presentKeys);
        const monthKeys: number[] = [];
        for (let k = maxKey - 5; k <= maxKey; k++) monthKeys.push(k);
        const monthIndex = new Map<number, number>();
        monthKeys.forEach((k, i) => monthIndex.set(k, i));
        const monthLabels = monthKeys.map(k => monthAbbr[k % 12]);

        const taskMap = new Map<string, { name: string; sums: number[]; counts: number[] }>();
        for (const task of tasks) {
            const key = task.taskName ?? task.parentAccountId ?? '';
            const col = monthIndex.get(task.doneYear * 12 + (task.doneMonth - 1));
            if (col === undefined) continue;
            let entry = taskMap.get(key);
            if (!entry) {
                entry = { name: task.taskName ?? key, sums: new Array(monthKeys.length).fill(0), counts: new Array(monthKeys.length).fill(0) };
                taskMap.set(key, entry);
            }
            entry.sums[col] += task.avgCycleTime ?? 0;
            entry.counts[col] += 1;
        }

        const series = Array.from(taskMap.entries())
            .sort((a, b) => (a[0] ?? '').localeCompare(b[0] ?? '', undefined, { numeric: true }))
            .map(([, v]) => ({
                label: v.name,
                data: v.sums.map((sum, i) => (v.counts[i] > 0 ? sum / v.counts[i] : 0))
            }));

        return { monthLabels, series };
    }

    toggleExpand(chartIndex: number): void {
        this.expandedChart = this.expandedChart === chartIndex ? null : chartIndex;
    }

    setTaskCycleTimeChartType(view: 'byTask' | 'byMonth'): void {
        this.taskCycleTimeChartType = view;
    }

    setAvgCycleTimePerQueueChartType(view: 'line' | 'bar'): void {
        const leavingChart = this.caseAvgCycleTimePerQueueChartType === 'line' ? this.caseCycleTimePerQueueLineChart : this.caseCycleTimePerQueueBarChart;
        const enteringChart = view === 'line' ? this.caseCycleTimePerQueueLineChart : this.caseCycleTimePerQueueBarChart;
        const hiddenIds = leavingChart?.getHiddenQueueIds() ?? [];

        this.caseAvgCycleTimePerQueueChartType = view;
        localStorage.setItem('avgCycleTimePerQueueChartType', view);

        setTimeout(() => enteringChart?.applyHiddenQueueIds(hiddenIds), 0);
    }

    loadAvgCycleTimePerQueueChartType(): void {
        const storedView = localStorage.getItem('avgCycleTimePerQueueChartType');
        if (storedView === 'line' || storedView === 'bar') {
            this.caseAvgCycleTimePerQueueChartType = storedView;
        }
    }

    hasHiddenCaseQueueLegendItems(): boolean {
        if (this.caseAvgCycleTimePerQueueChartType === 'line') {
            return this.caseCycleTimePerQueueLineChart?.hasHiddenDatasets() ?? false;
        }
        return this.caseCycleTimePerQueueBarChart?.hasHiddenDatasets() ?? false;
    }

    resetCaseQueueLegendSelections(): void {
        this.caseCycleTimePerQueueLineChart?.resetAllDatasets();
        this.caseCycleTimePerQueueBarChart?.resetAllDatasets();
    }

    hasHiddenCaseAvgCycleTimeLegendItems(): boolean {
        return this.caseAvgCycleTimeChart?.hasHiddenDatasets() ?? false;
    }

    resetCaseAvgCycleTimeLegendSelections(): void {
        this.caseAvgCycleTimeChart?.resetAllDatasets();
    }

    hasActiveTaskHeatMapSort(): boolean {
        return this.taskHeatMap?.hasActiveSort() ?? false;
    }

    resetTaskHeatMapSort(): void {
        this.taskHeatMap?.resetSort();
    }

    hasHiddenTaskCycleTimeLegendItems(): boolean {
        if (this.taskCycleTimeChartType === 'byTask') {
            return this.taskSixMonthChart?.hasHiddenDatasets() ?? false;
        }
        return this.taskByMonthChart?.hasHiddenDatasets() ?? false;
    }

    resetTaskCycleTimeLegendSelections(): void {
        this.taskSixMonthChart?.resetAllDatasets();
        this.taskByMonthChart?.resetAllDatasets();
    }

    sortByTeamMemberName<T extends { firstName: string; lastName: string }>(items: T[]): T[] {
        return [...items].sort((a, b) => {
            const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
            const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
            return nameA.localeCompare(nameB);
        });
    }
}
